'use client';

import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useDigitalScribe } from '@/lib/hooks/useDigitalScribe';

type ReviewStatus = 'pending' | 'accepted' | 'corrected' | 'flagged';

interface ExtractedEntity {
  id: string;
  type: 'date' | 'name' | 'place' | 'amount';
  value: string;
  confidence: number;
  verified: boolean;
}

interface Revision {
  id: string;
  version: number;
  savedAt: string;
  savedBy: string;
  changeNote: string;
  wordCount: number;
}

const mockRecord = {
  id: 'rec-pending-009',
  title: "Meeting Minutes — Emergent Communication, 14 April 1887",
  category: 'Lodge Records',
  uploadedAt: '02 May 2026',
  uploadedBy: 'Contributor Token',
  fileType: 'JPG',
  aiConfidence: 72,
};

const initialTranscription = `At an Emergent Communication of St. Andrew's Lodge No. 1, held at the Lodge Room, Halifax, Nova Scotia, on the fourteenth day of April, in the year of our Lord one thousand eight hundred and eighty-seven, and of Masonry 5887.

The Worshipful Master, Brother James Archibald Chisholm, presided.

Present: Senior Warden, Brother William Donald Fraser; Junior Warden, Brother Robert Angus MacLeod; Treasurer, Brother Thomas Gordon Henderson; Secretary, Brother Andrew James Sutherland; Senior Deacon, Brother Charles Edward MacPherson.

The Lodge was opened in due form on the Third Degree.

The purpose of the Communication was to consider the petition of Mr. George William Tanner, a candidate for the Mysteries and Privileges of Ancient Freemasonry.

The petition having been read, it was proposed by Brother Fraser and seconded by Brother MacLeod that the petition be received and the candidate be balloted for at the next Regular Communication.

The ballot having been taken, the result was declared favourable.

There being no further business, the Lodge was closed in peace and harmony.

[Signed] Andrew James Sutherland, Secretary`;

const initialEntities: ExtractedEntity[] = [
  { id: 'ent-001', type: 'date', value: '14 April 1887', confidence: 94, verified: true },
  { id: 'ent-002', type: 'name', value: 'James Archibald Chisholm', confidence: 88, verified: false },
  { id: 'ent-003', type: 'name', value: 'William Donald Fraser', confidence: 91, verified: true },
  { id: 'ent-004', type: 'name', value: 'Robert Angus MacLeod', confidence: 85, verified: false },
  { id: 'ent-005', type: 'name', value: 'Andrew James Sutherland', confidence: 92, verified: true },
  { id: 'ent-006', type: 'name', value: 'George William Tanner', confidence: 78, verified: false },
  { id: 'ent-007', type: 'place', value: 'Halifax, Nova Scotia', confidence: 96, verified: true },
  { id: 'ent-008', type: 'name', value: 'Thomas Gordon Henderson', confidence: 83, verified: false },
];

const initialRevisions: Revision[] = [
  { id: 'rev-001', version: 1, savedAt: '02 May 2026, 14:32', savedBy: 'AI — Digital Scribe', changeNote: 'Initial AI transcription', wordCount: 187 },
  { id: 'rev-002', version: 2, savedAt: '03 May 2026, 09:15', savedBy: 'R. Sinclair', changeNote: 'Corrected spelling of "Chisholm" and "MacPherson"', wordCount: 189 },
];

const entityTypeColors: Record<string, { bg: string; color: string; icon: string }> = {
  date: { bg: 'var(--info-bg)', color: 'var(--info)', icon: 'CalendarIcon' },
  name: { bg: 'rgba(201, 168, 76, 0.15)', color: '#7A5C1E', icon: 'UserIcon' },
  place: { bg: 'var(--success-bg)', color: 'var(--success)', icon: 'MapPinIcon' },
  amount: { bg: 'var(--warning-bg)', color: 'var(--warning)', icon: 'CurrencyDollarIcon' },
};

export default function TranscriptionReview() {
  const [transcription, setTranscription] = useState(initialTranscription);
  const [status, setStatus] = useState<ReviewStatus>('pending');
  const [entities, setEntities] = useState<ExtractedEntity[]>(initialEntities);
  const [revisions, setRevisions] = useState<Revision[]>(initialRevisions);
  const [isSaving, setIsSaving] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [activeTab, setActiveTab] = useState<'entities' | 'revisions' | 'metadata'>('entities');
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [aiConfidence, setAiConfidence] = useState(mockRecord.aiConfidence);
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { result: scribeResult, isAnalysing, error: scribeError, analyseFile } = useDigitalScribe();

  useEffect(() => {
    if (scribeError) {
      toast.error(`Digital Scribe: ${scribeError}`);
    }
  }, [scribeError]);

  useEffect(() => {
    if (!scribeResult) return;
    setTranscription(scribeResult.transcription);
    setAiConfidence(scribeResult.overallConfidence);
    const mapped: ExtractedEntity[] = scribeResult.entities.map((e, i) => ({
      id: `ent-ai-${i}`,
      type: e.type,
      value: e.value,
      confidence: e.confidence,
      verified: false,
    }));
    setEntities(mapped);
    const wc = scribeResult.transcription.trim().split(/\s+/).filter(Boolean).length;
    const newRev: Revision = {
      id: `rev-ai-${Date.now()}`,
      version: revisions.length + 1,
      savedAt: new Date().toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }),
      savedBy: 'AI — Digital Scribe (GPT-4o)',
      changeNote: 'Re-analysed via Digital Scribe engine',
      wordCount: wc,
    };
    setRevisions((r) => [...r, newRev]);
    toast.success(`Digital Scribe complete — ${scribeResult.overallConfidence}% confidence · ${mapped.length} entities extracted`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scribeResult]);

  const handleReanalyse = async () => {
    if (!uploadedImageFile) {
      fileInputRef.current?.click();
      return;
    }
    await analyseFile(uploadedImageFile);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, or WEBP)');
      return;
    }
    setUploadedImageFile(file);
    toast.info('Digital Scribe is analysing your document…');
    await analyseFile(file);
  };

  const wordCount = transcription.trim().split(/\s+/).filter(Boolean).length;
  const charCount = transcription.length;

  const handleSaveCorrection = async () => {
    setIsSaving(true);
    await new Promise((r) => setTimeout(r, 1200));
    const newRev: Revision = {
      id: `rev-${revisions.length + 10}`,
      version: revisions.length + 1,
      savedAt: '04 May 2026, 18:41',
      savedBy: 'R. Sinclair',
      changeNote: 'Secretary correction',
      wordCount,
    };
    setRevisions((r) => [...r, newRev]);
    setStatus('corrected');
    setIsSaving(false);
    toast.success('Correction saved as new revision — original version preserved');
  };

  const handleAccept = () => {
    setStatus('accepted');
    toast.success('Transcription accepted and record moved to Archive');
  };

  const handleFlag = () => {
    if (!flagReason.trim()) return;
    setStatus('flagged');
    setShowFlagModal(false);
    toast.warning('Record flagged for re-scan — Secretary will be notified');
  };

  const toggleEntityVerified = (id: string) => {
    setEntities((e) => e.map((en) => (en.id === id ? { ...en, verified: !en.verified } : en)));
  };

  const confidenceColor = (c: number) =>
    c >= 85 ? 'var(--success)' : c >= 70 ? 'var(--warning)' : 'var(--danger)';

  return (
    <div className="space-y-5">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".jpg,.jpeg,.png,.webp"
        onChange={handleImageUpload}
      />

      {/* Header bar */}
      <div className="masonic-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-serif text-lg font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                {mockRecord.title}
              </h2>
              <span
                className="status-badge"
                style={{
                  background:
                    status === 'accepted' ? 'var(--success-bg)' :
                    status === 'corrected' ? 'rgba(201, 168, 76, 0.15)' :
                    status === 'flagged' ? 'var(--danger-bg)' :
                    'var(--warning-bg)',
                  color:
                    status === 'accepted' ? 'var(--success)' :
                    status === 'corrected' ? '#7A5C1E' :
                    status === 'flagged' ? 'var(--danger)' :
                    'var(--warning)',
                }}
              >
                {status === 'pending' ? 'Pending Review' :
                 status === 'accepted' ? 'Accepted' :
                 status === 'corrected' ? 'Corrected & Saved' : 'Flagged for Re-scan'}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 mt-2">
              <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                <Icon name="CalendarIcon" size={13} />
                Uploaded {mockRecord.uploadedAt}
              </span>
              <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                <Icon name="UserIcon" size={13} />
                {mockRecord.uploadedBy}
              </span>
              <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                <Icon name="DocumentTextIcon" size={13} />
                {mockRecord.fileType}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
            <button
              className="masonic-btn-gold text-sm"
              onClick={handleReanalyse}
              disabled={isAnalysing}
              title={uploadedImageFile ? 'Re-run Digital Scribe on uploaded image' : 'Upload a document image to run Digital Scribe'}
            >
              {isAnalysing ? (
                <>
                  <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
                  Analysing…
                </>
              ) : (
                <>
                  <Icon name="SparklesIcon" size={15} />
                  {uploadedImageFile ? 'Re-analyse' : 'Analyse Document'}
                </>
              )}
            </button>

            <div
              className="flex items-center gap-3 px-4 py-3 rounded-lg"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
                  AI Confidence
                </p>
                <p className="text-2xl font-bold tabular-nums font-serif" style={{ color: confidenceColor(aiConfidence) }}>
                  {aiConfidence}%
                </p>
              </div>
              <div className="w-12 h-12 relative flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--border)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9" fill="none"
                    stroke={confidenceColor(aiConfidence)}
                    strokeWidth="3"
                    strokeDasharray={`${aiConfidence} ${100 - aiConfidence}`}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main split pane */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Left: Original document */}
        <div className="masonic-card overflow-hidden">
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}
          >
            <div className="flex items-center gap-2">
              <Icon name="PhotoIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Original Document</span>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-1.5 rounded hover:bg-card transition-colors" onClick={() => setZoom(Math.max(50, zoom - 25))} title="Zoom out">
                <Icon name="MinusIcon" size={14} />
              </button>
              <span className="text-xs tabular-nums px-2" style={{ color: 'var(--muted-foreground)', minWidth: 40, textAlign: 'center' }}>{zoom}%</span>
              <button className="p-1.5 rounded hover:bg-card transition-colors" onClick={() => setZoom(Math.min(200, zoom + 25))} title="Zoom in">
                <Icon name="PlusIcon" size={14} />
              </button>
              <button className="p-1.5 rounded hover:bg-card transition-colors ml-1" onClick={() => setZoom(100)} title="Reset zoom">
                <Icon name="ArrowsPointingInIcon" size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-auto p-4 scrollbar-thin" style={{ maxHeight: 560, background: '#E8E0D0' }}>
            <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left', width: `${10000 / zoom}%` }}>
              <div
                className="rounded-lg overflow-hidden shadow-masonic-lg"
                style={{
                  background: '#F9F4EA',
                  border: '1px solid var(--border)',
                  minHeight: 480,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem',
                }}
              >
                <Icon name="DocumentTextIcon" size={48} style={{ color: 'var(--border)', marginBottom: 16 } as React.CSSProperties} />
                <p className="font-serif text-center text-sm" style={{ color: 'var(--muted-foreground)', maxWidth: 300 }}>
                  Original scanned document
                </p>
                <p className="text-xs text-center mt-2" style={{ color: 'var(--muted-foreground)', maxWidth: 280 }}>
                  Meeting Minutes — Emergent Communication<br />
                  14 April 1887 · St. Andrew&apos;s Lodge No. 1<br />
                  Halifax, Nova Scotia
                </p>
                <div
                  className="mt-6 px-4 py-3 rounded-lg text-xs font-mono leading-relaxed"
                  style={{
                    background: 'rgba(27, 42, 74, 0.04)',
                    border: '1px solid var(--border)',
                    color: 'var(--muted-foreground)',
                    maxWidth: 360,
                    fontStyle: 'italic',
                  }}
                >
                  [Handwritten document scan would appear here. In production, the actual JPG/PDF file stored in the WORM archive is rendered in this viewer.]
                </div>
                <button
                  type="button"
                  className="masonic-btn-outline mt-6 text-sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon name="ArrowUpTrayIcon" size={15} />
                  Upload Document to Analyse
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 py-2 flex items-center gap-3" style={{ borderTop: '1px solid var(--border)', background: 'var(--muted)' }}>
            <button
              className="text-xs flex items-center gap-1.5 hover:underline"
              style={{ color: 'var(--accent)' }}
              onClick={() => toast.info('Full-resolution download initiated')}
            >
              <Icon name="ArrowDownTrayIcon" size={13} />
              Download original
            </button>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Version 1 of {revisions.length} · {mockRecord.fileType}
            </span>
          </div>
        </div>

        {/* Right: Transcription editor */}
        <div className="masonic-card overflow-hidden flex flex-col">
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}
          >
            <div className="flex items-center gap-2">
              <Icon name="SparklesIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>AI Transcription</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>Editable</span>
              {isAnalysing && (
                <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: 'rgba(201, 168, 76, 0.15)', color: '#7A5C1E' }}>
                  <Icon name="ArrowPathIcon" size={11} className="animate-spin" />
                  Analysing…
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              <span className="tabular-nums">{wordCount} words</span>
              <span className="mx-1">·</span>
              <span className="tabular-nums">{charCount} chars</span>
            </div>
          </div>

          <div className="flex-1 p-4">
            <textarea
              className="w-full h-full masonic-input resize-none font-sans text-sm leading-relaxed"
              style={{ minHeight: 460, fontFamily: 'var(--font-dm-sans)', lineHeight: 1.75 }}
              value={transcription}
              onChange={(e) => setTranscription(e.target.value)}
              placeholder="Upload a document image and click 'Analyse Document' to run the Digital Scribe engine…"
              disabled={isAnalysing}
            />
          </div>

          <div className="px-4 py-3 flex flex-wrap gap-2 flex-shrink-0" style={{ borderTop: '1px solid var(--border)' }}>
            <button
              className="masonic-btn-primary text-sm flex-1"
              onClick={handleSaveCorrection}
              disabled={isSaving || status === 'accepted'}
            >
              {isSaving ? (
                <><Icon name="ArrowPathIcon" size={15} className="animate-spin" />Saving revision…</>
              ) : (
                <><Icon name="DocumentCheckIcon" size={15} />Save Correction</>
              )}
            </button>
            <button className="masonic-btn-gold text-sm flex-1" onClick={handleAccept} disabled={status === 'accepted'}>
              <Icon name="CheckCircleIcon" size={15} />
              Accept &amp; Archive
            </button>
            <button className="masonic-btn-outline text-sm px-3" onClick={() => setShowFlagModal(true)} title="Flag this record for re-scan">
              <Icon name="FlagIcon" size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom panel: entities / revisions / metadata */}
      <div className="masonic-card overflow-hidden">
        <div className="flex" style={{ borderBottom: '1px solid var(--border)' }}>
          {(['entities', 'revisions', 'metadata'] as const).map((t) => (
            <button
              key={`tab-${t}`}
              onClick={() => setActiveTab(t)}
              className="px-5 py-3 text-sm font-semibold capitalize transition-colors"
              style={{
                borderBottom: activeTab === t ? '2px solid var(--accent)' : '2px solid transparent',
                color: activeTab === t ? 'var(--accent)' : 'var(--muted-foreground)',
                background: 'transparent',
              }}
            >
              {t === 'entities' ? `Extracted Entities (${entities.length})` :
               t === 'revisions' ? `Revision History (${revisions.length})` :
               'Record Metadata'}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === 'entities' && (
            <div>
              <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
                The Digital Scribe extracted these entities from the document. Verify each one before archiving.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {entities.map((ent) => {
                  const cfg = entityTypeColors[ent.type] ?? entityTypeColors.name;
                  return (
                    <div
                      key={ent.id}
                      className="rounded-lg p-3 transition-all"
                      style={{
                        background: ent.verified ? cfg.bg : 'var(--muted)',
                        border: `1px solid ${ent.verified ? 'transparent' : 'var(--border)'}`,
                        opacity: ent.verified ? 1 : 0.8,
                      }}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <Icon
                            name={cfg.icon as Parameters<typeof Icon>[0]['name']}
                            size={13}
                            style={{ color: ent.verified ? cfg.color : 'var(--muted-foreground)' } as React.CSSProperties}
                          />
                          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: ent.verified ? cfg.color : 'var(--muted-foreground)' }}>
                            {ent.type}
                          </span>
                        </div>
                        <button
                          onClick={() => toggleEntityVerified(ent.id)}
                          className="p-0.5 rounded transition-colors"
                          title={ent.verified ? 'Mark as unverified' : 'Mark as verified'}
                        >
                          <Icon name="CheckCircleIcon" size={14} style={{ color: ent.verified ? cfg.color : 'var(--border)' } as React.CSSProperties} />
                        </button>
                      </div>
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{ent.value}</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <div className="flex-1 h-1 rounded-full" style={{ background: 'var(--border)' }}>
                          <div className="h-1 rounded-full" style={{ width: `${ent.confidence}%`, background: confidenceColor(ent.confidence) }} />
                        </div>
                        <span className="text-xs tabular-nums" style={{ color: 'var(--muted-foreground)' }}>{ent.confidence}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'revisions' && (
            <div>
              <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
                All versions are retained permanently. The archive uses a write-once, read-many system — no revision is ever deleted.
              </p>
              <div className="space-y-3">
                {revisions.map((rev, idx) => (
                  <div key={rev.id} className="flex gap-4 items-start">
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{
                          background: idx === revisions.length - 1 ? 'var(--primary)' : 'var(--muted)',
                          color: idx === revisions.length - 1 ? 'var(--accent)' : 'var(--muted-foreground)',
                          border: idx === revisions.length - 1 ? 'none' : '1px solid var(--border)',
                        }}
                      >
                        v{rev.version}
                      </div>
                      {idx < revisions.length - 1 && <div className="w-0.5 h-6" style={{ background: 'var(--border)' }} />}
                    </div>
                    <div
                      className="flex-1 rounded-lg p-3"
                      style={{
                        background: idx === revisions.length - 1 ? 'rgba(201, 168, 76, 0.08)' : 'var(--muted)',
                        border: `1px solid ${idx === revisions.length - 1 ? 'rgba(201, 168, 76, 0.3)' : 'var(--border)'}`,
                      }}
                    >
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{rev.changeNote}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{rev.savedBy} · {rev.savedAt}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs tabular-nums" style={{ color: 'var(--muted-foreground)' }}>{rev.wordCount} words</span>
                          <button
                            className="text-xs px-2 py-1 rounded hover:bg-card transition-colors"
                            style={{ color: 'var(--accent)', border: '1px solid var(--border)' }}
                            onClick={() => toast.info(`Viewing version ${rev.version}`)}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg flex items-center gap-2 text-xs" style={{ background: 'var(--info-bg)', color: 'var(--info)', border: '1px solid rgba(27, 58, 107, 0.2)' }}>
                <Icon name="LockClosedIcon" size={13} />
                WORM policy active — all revisions are immutable and permanently retained
              </div>
            </div>
          )}

          {activeTab === 'metadata' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: 'Record ID', value: mockRecord.id },
                { label: 'Category', value: 'Lodge Records' },
                { label: 'Lodge', value: "St. Andrew's Lodge No. 1" },
                { label: 'District', value: 'District IV' },
                { label: 'Grand Lodge', value: 'Nova Scotia' },
                { label: 'Historical Date', value: entities.find((e) => e.type === 'date')?.value ?? '14 April 1887' },
                { label: 'Estimated Decade', value: '1880s' },
                { label: 'File Format', value: mockRecord.fileType },
                { label: 'Uploaded By', value: mockRecord.uploadedBy },
                { label: 'Upload Date', value: mockRecord.uploadedAt },
                { label: 'Total Revisions', value: String(revisions.length) },
                { label: 'AI Model', value: 'GPT-4o Vision' },
              ].map((item) => (
                <div key={`meta-${item.label}`} className="p-3 rounded-lg" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--muted-foreground)' }}>{item.label}</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{item.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Flag modal */}
      {showFlagModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFlagModal(false)} />
          <div className="relative masonic-card p-6 w-full max-w-md" style={{ zIndex: 10 }}>
            <h3 className="font-serif text-lg font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Flag for Re-scan</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
              Flagging this record will remove it from the review queue and notify the contributor that a higher-quality scan is needed.
            </p>
            <label className="masonic-label">Reason for flagging *</label>
            <textarea
              className="masonic-input resize-none mt-1 mb-4"
              rows={3}
              placeholder="e.g. Image too blurry to transcribe accurately — please re-photograph in better lighting"
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button className="masonic-btn-outline flex-1" onClick={() => setShowFlagModal(false)}>Cancel</button>
              <button
                className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid rgba(155, 28, 28, 0.3)' }}
                onClick={handleFlag}
                disabled={!flagReason.trim()}
              >
                <span className="flex items-center justify-center gap-2">
                  <Icon name="FlagIcon" size={15} />
                  Confirm Flag
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}